import { useId } from "react";
import { MdAdd, MdArrowDownward, MdArrowUpward, MdClose, MdDelete } from "react-icons/md";
import Card from "components/card";
import Button, { IconButton } from "components/button";
import { Checkbox, InputField, Select } from "components/fields";
import OptionGroup from "components/heartbeat/OptionGroup";
import { isChoiceQuestion, questionOptionsErrorKey, questionTextErrorKey } from "./validation";

const QUESTION_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "single_choice", label: "Single Choice" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "slider", label: "Slider (1-5)" },
];

const CATEGORY_OPTIONS = [
  { value: "faith_journey", label: "Faith Journey" },
  { value: "community", label: "Community" },
  { value: "life_challenges", label: "Life & Wellbeing" },
];

const QuestionCard = ({ editor, question, index }) => {
  const {
    formData,
    errors,
    updateQuestion,
    moveQuestion,
    removeQuestion,
    addOption,
    updateOption,
    removeOption,
  } = editor;
  const optionsError = errors[questionOptionsErrorKey(index)];
  const optionsErrorId = useId();

  return (
    <Card padding="md" className="gap-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-heading">Question {index + 1}</h3>
        <div className="flex items-center gap-1">
          <IconButton
            icon={<MdArrowUpward />}
            label="Move question up"
            size="sm"
            onClick={() => moveQuestion(index, index - 1)}
            disabled={index === 0}
          />
          <IconButton
            icon={<MdArrowDownward />}
            label="Move question down"
            size="sm"
            onClick={() => moveQuestion(index, index + 1)}
            disabled={index === formData.questions.length - 1}
          />
          <IconButton
            icon={<MdDelete />}
            label="Remove question"
            size="sm"
            onClick={() => removeQuestion(index)}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <InputField
          label="Question Text"
          required
          className="md:col-span-2"
          value={question.text}
          onChange={(e) => updateQuestion(index, "text", e.target.value)}
          placeholder="Enter your question..."
          error={errors[questionTextErrorKey(index)]}
        />
        <Select
          label="Question Type"
          value={question.type}
          onChange={(e) => updateQuestion(index, "type", e.target.value)}
          options={QUESTION_TYPES}
        />
        <div className="flex items-center md:pt-6">
          <Checkbox
            label="Required question"
            checked={question.required}
            onChange={(e) => updateQuestion(index, "required", e.target.checked)}
          />
        </div>
      </div>

      {isChoiceQuestion(question) && (
        <OptionGroup label="Options">
          <div className="space-y-2">
            {(question.options || []).map((option, optIndex) => (
              <div key={optIndex} className="flex items-center gap-2">
                <InputField
                  aria-label={`Option ${optIndex + 1}`}
                  className="flex-1"
                  value={option}
                  onChange={(e) => updateOption(index, optIndex, e.target.value)}
                  placeholder={`Option ${optIndex + 1}`}
                  state={optionsError && !(option && option.trim()) ? "error" : undefined}
                />
                <IconButton
                  icon={<MdClose />}
                  label="Remove option"
                  onClick={() => removeOption(index, optIndex)}
                />
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<MdAdd aria-hidden="true" className="h-4 w-4" />}
              onClick={() => addOption(index)}
              data-invalid-focus={optionsError ? "true" : undefined}
              aria-describedby={optionsError ? optionsErrorId : undefined}
            >
              Add Option
            </Button>
          </div>
          {optionsError && (
            <p id={optionsErrorId} className="mt-1 text-xs text-danger-fg">
              {optionsError}
            </p>
          )}
        </OptionGroup>
      )}

      {question.type === "slider" && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <InputField
              label="Low Label (Left)"
              value={question.lowLabel || ""}
              onChange={(e) => updateQuestion(index, "lowLabel", e.target.value)}
              placeholder="e.g., Rarely, Never, Not at all"
            />
            <InputField
              label="High Label (Right)"
              value={question.highLabel || ""}
              onChange={(e) => updateQuestion(index, "highLabel", e.target.value)}
              placeholder="e.g., Always, Very much, Daily"
            />
          </div>
          {formData.surveyType === "spiritual" && (
            <Select
              label="Category"
              value={question.category || ""}
              onChange={(e) => updateQuestion(index, "category", e.target.value)}
              hint="Category determines the color accent and is used for scoring"
            >
              <option value="">Select category...</option>
              {CATEGORY_OPTIONS.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}
    </Card>
  );
};

export default QuestionCard;
